import { CtxDataFile } from 'interface/file/file.interface';
import { StatusGroup } from './fileStatusGrouping';

export type HeaderActionKind =
  | 'pause-selected'
  | 'resume-selected'
  | 'process-selected'
  | 'retry-selected'
  | 'process-selected-mixed'
  | 'pause-all'
  | 'resume-all'
  | 'retry-all'
  | 'process-all';

export type HeaderActionVariant = 'pause' | 'primary';
export type HeaderActionIcon = 'pause' | 'play';

export interface HeaderActionSpec {
  key: string;
  kind: HeaderActionKind;
  label: string;
  variant: HeaderActionVariant;
  icon: HeaderActionIcon;
  showLoadingWhenRunning?: boolean;
  tooltip?: string;
  fileIds?: string[];
}

export interface HeaderActionContext {
  isJourneyActive: boolean;
  hasSelection: boolean;
  showProcessingUi: boolean;
  selectedByGroup: Record<StatusGroup, string[]>;
  filesByGroup: Record<StatusGroup, CtxDataFile[]>;
  readyCount: number;
}

function selectedAction(
  ids: string[],
  spec: Omit<HeaderActionSpec, 'key' | 'fileIds'>,
): HeaderActionSpec {
  return { ...spec, fileIds: ids };
}

function resolveSelectedActions(ctx: HeaderActionContext): HeaderActionSpec[] {
  const { processing, paused, ready, failed } = ctx.selectedByGroup;

  if (processing.length > 0) {
    return [
      selectedAction(processing, {
        key: 'pause-selected',
        kind: 'pause-selected',
        label: `Pause selected (${processing.length})`,
        variant: 'pause',
        icon: 'pause',
        tooltip: 'Pause selected files',
      }),
    ];
  }
  if (paused.length > 0) {
    return [
      selectedAction(paused, {
        key: 'resume-selected',
        kind: 'resume-selected',
        label: `Resume selected (${paused.length})`,
        variant: 'primary',
        icon: 'play',
      }),
    ];
  }
  if (ready.length > 0) {
    return [
      selectedAction(ready, {
        key: 'process-selected',
        kind: 'process-selected',
        label: `Process selected (${ready.length})`,
        variant: 'primary',
        icon: 'play',
        showLoadingWhenRunning: true,
      }),
    ];
  }
  if (failed.length > 0) {
    return [
      selectedAction(failed, {
        key: 'retry-selected',
        kind: 'retry-selected',
        label: `Retry selected (${failed.length})`,
        variant: 'primary',
        icon: 'play',
        showLoadingWhenRunning: true,
      }),
    ];
  }

  return [];
}

function resolveBulkActions(ctx: HeaderActionContext): HeaderActionSpec[] {
  if (ctx.showProcessingUi) {
    return [
      {
        key: 'pause-all',
        kind: 'pause-all',
        label: 'Pause processing',
        variant: 'pause',
        icon: 'pause',
        tooltip: 'Pause processing',
      },
    ];
  }

  if (ctx.filesByGroup.paused.length > 0) {
    return [
      {
        key: 'resume-all',
        kind: 'resume-all',
        label: 'Resume processing',
        variant: 'primary',
        icon: 'play',
      },
    ];
  }

  const failedCount = ctx.filesByGroup.failed.length;
  const readyFileCount = ctx.filesByGroup.ready.length;

  switch (true) {
    case failedCount > 0 && readyFileCount > 0:
      return [
        {
          key: 'retry-all',
          kind: 'retry-all',
          label: `Retry all (${failedCount})`,
          variant: 'primary',
          icon: 'play',
          showLoadingWhenRunning: true,
        },
        {
          key: 'process-all',
          kind: 'process-all',
          label: `Process files (${ctx.readyCount})`,
          variant: 'primary',
          icon: 'play',
          showLoadingWhenRunning: true,
        },
      ];
    case failedCount > 0:
      return [
        {
          key: 'retry-all',
          kind: 'retry-all',
          label: `Retry all (${failedCount})`,
          variant: 'primary',
          icon: 'play',
          showLoadingWhenRunning: true,
        },
      ];
    case readyFileCount > 0:
      return [
        {
          key: 'process-all',
          kind: 'process-all',
          label: `Process files (${ctx.readyCount})`,
          variant: 'primary',
          icon: 'play',
          showLoadingWhenRunning: true,
        },
      ];
    default:
      return [];
  }
}

export function resolveHeaderActions(ctx: HeaderActionContext): HeaderActionSpec[] {
  if (!ctx.isJourneyActive) return [];
  if (ctx.hasSelection && !ctx.showProcessingUi) {
    // Collapse paused/ready/failed selections into one button that fans out the
    // correct backend action per group (RESUME for paused, PROCESS for
    // ready/failed). Only applies when no processing files are selected — those
    // need a Pause action and are handled by resolveSelectedActions below.
    const { processing, paused, ready, failed } = ctx.selectedByGroup;
    if (processing.length === 0) {
      const totalCount = paused.length + ready.length + failed.length;
      if (totalCount > 0) {
        return [
          {
            key: 'process-selected-mixed',
            kind: 'process-selected-mixed',
            label: `Process selected files (${totalCount})`,
            variant: 'primary',
            icon: 'play',
            showLoadingWhenRunning: true,
          },
        ];
      }
    }
  }
  if (ctx.hasSelection) return resolveSelectedActions(ctx);
  return resolveBulkActions(ctx);
}
